import { useParams, useNavigate, Link } from "react-router-dom";
import { getCookies } from "cookies-next";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { config } from "../../data/config";
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import { useTranslation } from "react-i18next";

const STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;

const STATUS_STYLE: Record<string, string> = {
    pending: "bg-amber-50 text-amber-800 ring-amber-200",
    approved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    rejected: "bg-rose-50 text-rose-800 ring-rose-200",
    cancelled: "bg-zinc-100 text-zinc-600 ring-zinc-200",
};

const inputClass =
    "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";

const cardClass =
    "rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 sm:p-5";

function toLocalInputValue(date: Date) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function addDaysAt(hour: number, days: number) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(hour, 0, 0, 0);
    return toLocalInputValue(d);
}

function whatsappHref(raw: string) {
    const digits = raw.replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : undefined;
}

export default function MeterSaleDetail() {
    const { t } = useTranslation();
    const { Id } = useParams();
    const navigate = useNavigate();
    const cookie = getCookies();
    const auth = { Authorization: "Bearer " + cookie["token"] };

    const [item, setItem] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [noteBusy, setNoteBusy] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);
    const [activityTab, setActivityTab] = useState<"followups" | "notes">("followups");
    const [form, setForm] = useState({
        fullName: "",
        country: "",
        mobile: "",
        whatsapp: "",
        email: "",
        nationalId: "",
        note: "",
        meters: 1,
        status: "pending",
    });
    const [noteText, setNoteText] = useState("");
    const [followNote, setFollowNote] = useState("");
    const [followAt, setFollowAt] = useState(addDaysAt(10, 1));

    const breadcrump = [
        { name: t("Dashboard"), url: "/dashboard" },
        { name: t("Real Estate System"), url: "/dashboard/RealEstate" },
        { name: t("Meter sale requests"), url: "/dashboard/RealEstate/meter-sales" },
        { name: t("Request detail"), url: null },
    ];

    const applyItem = (data: any) => {
        setItem(data);
        setForm({
            fullName: data.fullName || "",
            country: data.country || "",
            mobile: data.mobile || "",
            whatsapp: data.whatsapp || "",
            email: data.email || "",
            nationalId: data.nationalId || "",
            note: data.note || "",
            meters: data.meters || 1,
            status: data.status || "pending",
        });
    };

    const load = async () => {
        if (!Id) return;
        setLoading(true);
        try {
            const res = await axios.get(`${config.url}/v1/dashboard/realestate/meter-sales/${Id}`, {
                headers: auth,
            });
            applyItem(res.data.message);
        } catch (_) {
            Swal.fire(t("Error"), t("Could not load request."), "error");
            navigate("/dashboard/RealEstate/meter-sales");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Id]);

    const sortedNotes = useMemo(() => {
        const list = Array.isArray(item?.adminNotes) ? [...item.adminNotes] : [];
        return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }, [item]);

    const openFollowUps = useMemo(() => {
        const list = Array.isArray(item?.followUps) ? item.followUps.filter((f: any) => !f.completed) : [];
        return [...list].sort(
            (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
        );
    }, [item]);

    const doneFollowUps = useMemo(() => {
        const list = Array.isArray(item?.followUps) ? item.followUps.filter((f: any) => f.completed) : [];
        return [...list].sort(
            (a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
        );
    }, [item]);

    const flashSaved = () => {
        setSavedFlash(true);
        window.setTimeout(() => setSavedFlash(false), 1800);
    };

    const saveDetails = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!form.mobile.trim() && !form.whatsapp.trim()) {
            Swal.fire(t("Error"), t("Phone or WhatsApp is required"), "error");
            return;
        }
        setSaving(true);
        try {
            const res = await axios.put(
                `${config.url}/v1/dashboard/realestate/meter-sales/${Id}`,
                { ...form, meters: Number(form.meters) },
                { headers: auth }
            );
            applyItem(res.data.data);
            flashSaved();
        } catch (err: any) {
            Swal.fire(t("Error"), err?.response?.data?.message || t("Could not update request."), "error");
        } finally {
            setSaving(false);
        }
    };

    const quickStatus = async (status: string) => {
        setSaving(true);
        try {
            const res = await axios.put(
                `${config.url}/v1/dashboard/realestate/meter-sales/${Id}`,
                { status },
                { headers: auth }
            );
            applyItem(res.data.data);
            flashSaved();
        } catch (err: any) {
            Swal.fire(t("Error"), err?.response?.data?.message || t("Could not update request."), "error");
        } finally {
            setSaving(false);
        }
    };

    const addNote = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!noteText.trim()) return;
        setNoteBusy(true);
        try {
            const res = await axios.post(
                `${config.url}/v1/dashboard/realestate/meter-sales/${Id}/notes`,
                { text: noteText.trim() },
                { headers: auth }
            );
            applyItem(res.data.data);
            setNoteText("");
            setActivityTab("notes");
        } catch (err: any) {
            Swal.fire(t("Error"), err?.response?.data?.message || t("Could not add note."), "error");
        } finally {
            setNoteBusy(false);
        }
    };

    const addFollowUp = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!followAt) {
            Swal.fire(t("Error"), t("Follow-up date/time is required"), "error");
            return;
        }
        setFollowBusy(true);
        try {
            const res = await axios.post(
                `${config.url}/v1/dashboard/realestate/meter-sales/${Id}/follow-ups`,
                {
                    note: followNote.trim(),
                    scheduledAt: new Date(followAt).toISOString(),
                },
                { headers: auth }
            );
            applyItem(res.data.data);
            setFollowNote("");
            setFollowAt(addDaysAt(10, 1));
            setActivityTab("followups");
        } catch (err: any) {
            Swal.fire(t("Error"), err?.response?.data?.message || t("Could not add follow-up."), "error");
        } finally {
            setFollowBusy(false);
        }
    };

    const toggleFollowUp = async (followUpId: string, completed: boolean) => {
        try {
            const res = await axios.put(
                `${config.url}/v1/dashboard/realestate/meter-sales/${Id}/follow-ups/${followUpId}`,
                { completed },
                { headers: auth }
            );
            applyItem(res.data.data);
        } catch (err: any) {
            Swal.fire(t("Error"), err?.response?.data?.message || t("Could not update follow-up."), "error");
        }
    };

    const deleteFollowUp = async (followUpId: string) => {
        const ok = await Swal.fire({
            title: t("Delete follow-up?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t("Delete"),
            cancelButtonText: t("Cancel"),
        });
        if (!ok.isConfirmed) return;
        try {
            const res = await axios.delete(
                `${config.url}/v1/dashboard/realestate/meter-sales/${Id}/follow-ups/${followUpId}`,
                { headers: auth }
            );
            applyItem(res.data.data);
        } catch (err: any) {
            Swal.fire(t("Error"), err?.response?.data?.message || t("Could not delete follow-up."), "error");
        }
    };

    if (loading) {
        return (
            <div className="text-left bg-white dark:bg-zinc-900 p-3 m-4 directions min-h-[100vh] mb-5">
                <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-16 text-center text-sm text-zinc-500 dark:border-zinc-700">
                    {t("Loading...")}
                </div>
            </div>
        );
    }

    if (!item) return null;

    const wa = whatsappHref(form.whatsapp || form.mobile || "");

    return (
        <div className="text-left bg-zinc-50 dark:bg-zinc-950 p-3 m-4 directions min-h-[100vh] mb-5">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={`${t("Request detail")} · ${item.property?.code || "—"}`} />

            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-700 dark:bg-zinc-900 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-lg font-semibold text-zinc-900 dark:text-zinc-50">
                            {form.fullName || t("Unnamed lead")}
                        </h2>
                        <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLE[form.status] || STATUS_STYLE.pending}`}
                        >
                            {t(form.status)}
                        </span>
                        {savedFlash ? (
                            <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                                {t("Saved")}
                            </span>
                        ) : null}
                    </div>
                    <p className="mt-1 text-sm text-zinc-500">
                        {item.property?.code || "—"} ·{" "}
                        {item.method === "residential_session" ? t("Residential session") : t("Investment")} ·{" "}
                        {form.meters} m² · {t("Created")}{" "}
                        {item.createdAt ? new Date(item.createdAt).toLocaleString() : "—"}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link
                        to="/dashboard/RealEstate/meter-sales"
                        className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                    >
                        {t("Back to list")}
                    </Link>
                    {form.status === "pending" && (
                        <>
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => quickStatus("approved")}
                                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                                {t("Approve")}
                            </button>
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => quickStatus("rejected")}
                                className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-60"
                            >
                                {t("Reject")}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {form.mobile ? (
                    <a
                        href={`tel:${form.mobile.replace(/\s/g, "")}`}
                        className="rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm transition hover:border-blue-300 dark:border-zinc-700 dark:bg-zinc-900"
                    >
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Phone")}</p>
                        <p className="mt-1 text-sm font-semibold text-blue-700 dark:text-blue-400">{form.mobile}</p>
                    </a>
                ) : (
                    <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-3 dark:border-zinc-700">
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Phone")}</p>
                        <p className="mt-1 text-sm text-zinc-400">—</p>
                    </div>
                )}
                {wa ? (
                    <a
                        href={wa}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm transition hover:border-emerald-300 dark:border-zinc-700 dark:bg-zinc-900"
                    >
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t("WhatsApp")}</p>
                        <p className="mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                            {form.whatsapp || form.mobile}
                        </p>
                    </a>
                ) : (
                    <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-3 dark:border-zinc-700">
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t("WhatsApp")}</p>
                        <p className="mt-1 text-sm text-zinc-400">—</p>
                    </div>
                )}
                {form.email ? (
                    <a
                        href={`mailto:${form.email}`}
                        className="rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm transition hover:border-indigo-300 dark:border-zinc-700 dark:bg-zinc-900"
                    >
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Email")}</p>
                        <p className="mt-1 truncate text-sm font-semibold text-indigo-700 dark:text-indigo-400">
                            {form.email}
                        </p>
                    </a>
                ) : (
                    <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-3 dark:border-zinc-700">
                        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t("Email")}</p>
                        <p className="mt-1 text-sm text-zinc-400">—</p>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                <form onSubmit={saveDetails} className={`${cardClass} space-y-4 xl:col-span-5`}>
                    <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                            {t("Edit request")}
                        </h3>
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                            {saving ? t("Saving...") : t("Save changes")}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="block text-sm sm:col-span-1">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("Status")}</span>
                            <select
                                className={inputClass}
                                value={form.status}
                                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                            >
                                {STATUSES.map((status) => (
                                    <option key={status} value={status}>
                                        {t(status)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block text-sm">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("Meters")}</span>
                            <input
                                type="number"
                                min={1}
                                className={inputClass}
                                value={form.meters}
                                onChange={(e) => setForm((prev) => ({ ...prev, meters: Number(e.target.value) }))}
                            />
                        </label>
                        <label className="block text-sm sm:col-span-2">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("Full name")}</span>
                            <input
                                className={inputClass}
                                value={form.fullName}
                                onChange={(e) => setForm((prev) => ({ ...prev, fullName: e.target.value }))}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("Country")}</span>
                            <input
                                className={inputClass}
                                value={form.country}
                                onChange={(e) => setForm((prev) => ({ ...prev, country: e.target.value }))}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("National ID")}</span>
                            <input
                                className={inputClass}
                                value={form.nationalId}
                                onChange={(e) => setForm((prev) => ({ ...prev, nationalId: e.target.value }))}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("Phone")}</span>
                            <input
                                className={inputClass}
                                value={form.mobile}
                                onChange={(e) => setForm((prev) => ({ ...prev, mobile: e.target.value }))}
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("WhatsApp")}</span>
                            <input
                                className={inputClass}
                                value={form.whatsapp}
                                onChange={(e) => setForm((prev) => ({ ...prev, whatsapp: e.target.value }))}
                            />
                        </label>
                        <label className="block text-sm sm:col-span-2">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">{t("Email")}</span>
                            <input
                                type="email"
                                className={inputClass}
                                value={form.email}
                                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                            />
                        </label>
                        <label className="block text-sm sm:col-span-2">
                            <span className="mb-1.5 block font-medium text-zinc-700 dark:text-zinc-300">
                                {t("Customer note")}
                            </span>
                            <textarea
                                rows={3}
                                className={inputClass}
                                value={form.note}
                                onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                            />
                        </label>
                    </div>
                </form>

                <div className="space-y-5 xl:col-span-7">
                    <section className={cardClass}>
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-500">
                                {t("Activity")}
                            </h3>
                            <div className="inline-flex rounded-lg bg-zinc-100 p-1 dark:bg-zinc-800">
                                <button
                                    type="button"
                                    onClick={() => setActivityTab("followups")}
                                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                                        activityTab === "followups"
                                            ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-50"
                                            : "text-zinc-500 hover:text-zinc-800"
                                    }`}
                                >
                                    {t("Follow-ups")} ({openFollowUps.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActivityTab("notes")}
                                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                                        activityTab === "notes"
                                            ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-50"
                                            : "text-zinc-500 hover:text-zinc-800"
                                    }`}
                                >
                                    {t("Admin notes")} ({sortedNotes.length})
                                </button>
                            </div>
                        </div>

                        {activityTab === "followups" ? (
                            <div className="space-y-4">
                                <form onSubmit={addFollowUp} className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 dark:border-indigo-900/40 dark:bg-indigo-950/20">
                                    <p className="mb-2 text-sm font-medium text-indigo-900 dark:text-indigo-200">
                                        {t("Schedule follow-up")}
                                    </p>
                                    <div className="mb-2 flex flex-wrap gap-2">
                                        {[
                                            { label: t("Tomorrow 10:00"), value: addDaysAt(10, 1) },
                                            { label: t("In 2 days"), value: addDaysAt(10, 2) },
                                            { label: t("Next week"), value: addDaysAt(10, 7) },
                                        ].map((preset) => (
                                            <button
                                                key={preset.label}
                                                type="button"
                                                onClick={() => setFollowAt(preset.value)}
                                                className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-50 dark:bg-zinc-800 dark:text-indigo-300 dark:ring-indigo-900"
                                            >
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        <input
                                            type="datetime-local"
                                            className={inputClass}
                                            value={followAt}
                                            onChange={(e) => setFollowAt(e.target.value)}
                                            required
                                        />
                                        <input
                                            className={inputClass}
                                            value={followNote}
                                            onChange={(e) => setFollowNote(e.target.value)}
                                            placeholder={t("What to do...")}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={followBusy}
                                        className="mt-3 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                                    >
                                        {followBusy ? t("Saving...") : t("Add follow-up")}
                                    </button>
                                </form>

                                {openFollowUps.length === 0 ? (
                                    <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-8 text-center text-sm text-zinc-500 dark:border-zinc-700">
                                        {t("No open follow-ups. Schedule the next call or message.")}
                                    </p>
                                ) : (
                                    <ul className="space-y-2">
                                        {openFollowUps.map((f: any) => {
                                            const overdue = new Date(f.scheduledAt).getTime() < Date.now();
                                            return (
                                                <li
                                                    key={f._id}
                                                    className={`rounded-lg border px-3 py-3 ${
                                                        overdue
                                                            ? "border-rose-200 bg-rose-50/70 dark:border-rose-900/50 dark:bg-rose-950/20"
                                                            : "border-zinc-200 bg-zinc-50/80 dark:border-zinc-700 dark:bg-zinc-800/40"
                                                    }`}
                                                >
                                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                                        <div>
                                                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                                                                {f.note || t("Follow-up")}
                                                            </p>
                                                            <p className={`mt-1 text-xs ${overdue ? "font-medium text-rose-700" : "text-zinc-500"}`}>
                                                                {new Date(f.scheduledAt).toLocaleString()}
                                                                {overdue ? ` · ${t("Overdue")}` : ""}
                                                                {f.createdByName ? ` · ${f.createdByName}` : ""}
                                                            </p>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
                                                                onClick={() => toggleFollowUp(f._id, true)}
                                                            >
                                                                {t("Mark done")}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="rounded-lg border border-zinc-200 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:border-zinc-600"
                                                                onClick={() => deleteFollowUp(f._id)}
                                                            >
                                                                {t("Delete")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {doneFollowUps.length > 0 && (
                                    <div>
                                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
                                            {t("Completed")} ({doneFollowUps.length})
                                        </p>
                                        <ul className="space-y-2 opacity-80">
                                            {doneFollowUps.map((f: any) => (
                                                <li
                                                    key={f._id}
                                                    className="flex flex-col gap-2 rounded-lg border border-zinc-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between dark:border-zinc-700"
                                                >
                                                    <div>
                                                        <p className="text-sm text-zinc-500 line-through">{f.note || t("Follow-up")}</p>
                                                        <p className="text-xs text-zinc-400">
                                                            {new Date(f.scheduledAt).toLocaleString()}
                                                            {f.completedAt
                                                                ? ` · ${t("Done")} ${new Date(f.completedAt).toLocaleString()}`
                                                                : ""}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="text-xs font-medium text-zinc-600 underline"
                                                        onClick={() => toggleFollowUp(f._id, false)}
                                                    >
                                                        {t("Mark open")}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <form onSubmit={addNote} className="rounded-lg border border-teal-100 bg-teal-50/40 p-3 dark:border-teal-900/40 dark:bg-teal-950/20">
                                    <textarea
                                        rows={3}
                                        className={inputClass}
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                        placeholder={t("Add a note...")}
                                    />
                                    <button
                                        type="submit"
                                        disabled={noteBusy || !noteText.trim()}
                                        className="mt-3 rounded-lg bg-teal-700 px-3 py-2 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-60"
                                    >
                                        {noteBusy ? t("Saving...") : t("Add note")}
                                    </button>
                                </form>
                                {sortedNotes.length === 0 ? (
                                    <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-8 text-center text-sm text-zinc-500 dark:border-zinc-700">
                                        {t("No notes yet.")}
                                    </p>
                                ) : (
                                    <ul className="space-y-2">
                                        {sortedNotes.map((n: any) => (
                                            <li
                                                key={n._id}
                                                className="rounded-lg border border-zinc-200 bg-zinc-50/80 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/40"
                                            >
                                                <p className="whitespace-pre-wrap text-sm text-zinc-900 dark:text-zinc-100">
                                                    {n.text}
                                                </p>
                                                <p className="mt-1 text-xs text-zinc-500">
                                                    {n.createdAt ? new Date(n.createdAt).toLocaleString() : ""}
                                                    {n.createdByName ? ` · ${n.createdByName}` : ""}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
